import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Valore data assente o non serializzabile (null, stringhe placeholder, dayjs invalido).
 * @param {unknown} value
 * @returns {boolean}
 */
export function isAbsentFormDate(value) {
  if (value == null || value === '') return true
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return true
    const lower = trimmed.toLowerCase()
    if (lower === 'null' || lower === 'undefined' || lower === 'invalid date') return true
    if (trimmed.toUpperCase() === 'NULL') return true
  }
  if (dayjs.isDayjs(value)) return !value.isValid()
  if (value instanceof Date) return Number.isNaN(value.getTime())
  return false
}

/**
 * Valore form/picker → ISO UTC per API (`…Z`).
 * @param {unknown} value
 * @returns {string | null}
 */
export function serializeApiDate(value) {
  if (isAbsentFormDate(value)) return null
  const d = dayjs.isDayjs(value) ? value : dayjs(value)
  if (!d.isValid()) return null
  return d.utc().toISOString()
}

/**
 * ISO UTC da API → dayjs in timezone locale per picker/display.
 * @param {unknown} iso
 * @returns {import('dayjs').Dayjs | null}
 */
export function parseApiDate(iso) {
  if (isAbsentFormDate(iso)) return null
  if (dayjs.isDayjs(iso)) return iso.isValid() ? iso : null
  const d = dayjs.utc(iso)
  return d.isValid() ? d.local() : null
}

/**
 * @param {unknown} value
 * @param {import('dayjs').Dayjs | null} [emptyFallback]
 * @returns {import('dayjs').Dayjs | null}
 */
export function toFormPickerValue(value, emptyFallback = null) {
  return parseApiDate(value) ?? emptyFallback
}

/**
 * @param {(value: string | null) => void} onChange
 * @returns {(date: import('dayjs').Dayjs | null) => void}
 */
export function fromFormPickerChange(onChange) {
  return (date) => onChange(serializeApiDate(date))
}

/**
 * Imposta Report date / Crash date se mancanti o non validi (nuovo incidente o placeholder).
 * @param {Record<string, unknown>} crashSection
 * @returns {Record<string, unknown>}
 */
export function stampCrashSectionDates(crashSection) {
  if (!crashSection || typeof crashSection !== 'object') return crashSection
  const out = { ...crashSection }
  const now = dayjs()
  if (isAbsentFormDate(out['Report date'])) {
    out['Report date'] = serializeApiDate(now)
  }
  if (isAbsentFormDate(out['Crash date'])) {
    out['Crash date'] = serializeApiDate(now)
  }
  return out
}

/**
 * Sincronizza date crash in react-hook-form quando assenti.
 * @param {(name: string, value: unknown, options?: object) => void} setValue
 * @param {(name?: string) => unknown} getValues
 */
export function stampCrashFormDatesIfAbsent(setValue, getValues) {
  const crash = getValues('crash')
  if (!crash || typeof crash !== 'object') return
  const stamped = stampCrashSectionDates(crash)
  if (stamped['Report date'] !== crash['Report date']) {
    setValue('crash.Report date', stamped['Report date'], { shouldDirty: false })
  }
  if (stamped['Crash date'] !== crash['Crash date']) {
    setValue('crash.Crash date', stamped['Crash date'], { shouldDirty: false })
  }
}
